import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { GitLabProject, GitLabStatus } from '../types';

// ═══════════════════════════════════════════════════════════
//  GitLabPanel
//
//  Two sections:
//    1. Доступные репо — GET /api/gitlab/available-projects
//       auto-loads on mount; user picks role + clicks Подключить.
//    2. Подключённые — уже в БД, использует Claude для работы.
// ═══════════════════════════════════════════════════════════

const API = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/+$/, '');

interface AvailableProject {
  gitlabId: number;
  name: string;
  nameWithNs: string;
  pathWithNs: string;
  url: string;
  defaultBranch: string;
  lastActivity: string;
  visibility: string;
  description: string;
  connected: boolean;
}

type Role = 'backend' | 'frontend' | 'docs' | 'mobile' | 'infra' | 'other';

const ROLE_OPTIONS: Role[] = ['backend', 'frontend', 'docs', 'mobile', 'infra', 'other'];

const relTime = (iso: string): string => {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60)    return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60)    return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)    return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30)    return `${d}d ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12)   return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
};

interface GitLabStatusDetailed extends GitLabStatus {
  envPresent?: boolean;
  tokenPrefix?: string | null;
  tokenValid?: boolean;
  tokenUser?: string;
  tokenError?: string;
}

const PAGE_SIZE = 25;

// Back-compat: the API used to return a bare AvailableProject[];
// it now returns { items, page, total, hasNext, ... }. Accept both
// so a stale frontend talking to a fresh backend doesn't blank out.
interface PageResult {
  items: AvailableProject[];
  page: number;
  perPage: number;
  total: number | null;
  totalPages: number | null;
  hasNext: boolean;
}

function normalizePage(data: any): PageResult {
  if (Array.isArray(data)) {
    return { items: data, page: 1, perPage: data.length, total: data.length, totalPages: 1, hasNext: false };
  }
  return {
    items: Array.isArray(data.items) ? data.items : [],
    page: Number(data.page) || 1,
    perPage: Number(data.perPage) || PAGE_SIZE,
    total: data.total ?? null,
    totalPages: data.totalPages ?? null,
    hasNext: !!data.hasNext,
  };
}

export const GitLabPanel: React.FC = () => {
  const [status, setStatus] = useState<GitLabStatusDetailed>({ connected: false, url: '', projects: [] });
  const [available, setAvailable] = useState<AvailableProject[]>([]);
  const [pageInfo, setPageInfo] = useState<{ page: number; total: number | null; totalPages: number | null; hasNext: boolean }>(
    { page: 1, total: null, totalPages: null, hasNext: false },
  );
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [connecting, setConnecting] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<Record<string, Role>>({});
  const [manualUrl, setManualUrl] = useState('');
  const [adding, setAdding] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/gitlab/status`);
      if (r.ok) setStatus(await r.json());
    } catch {}
  }, []);

  const loadAvailable = useCallback(async (q?: string, p: number = 1) => {
    setLoading(true);
    setLoadError(null);
    try {
      const url = new URL(`${API}/api/gitlab/available-projects`);
      if (q) url.searchParams.set('search', q);
      url.searchParams.set('page', String(p));
      url.searchParams.set('limit', String(PAGE_SIZE));
      const r = await fetch(url.toString());
      const data = await r.json();
      if (!r.ok) {
        setLoadError(data.message || data.error || `HTTP ${r.status}`);
        setAvailable([]);
        setPageInfo({ page: 1, total: null, totalPages: null, hasNext: false });
      } else {
        const norm = normalizePage(data);
        setAvailable(norm.items);
        setPageInfo({ page: norm.page, total: norm.total, totalPages: norm.totalPages, hasNext: norm.hasNext });
      }
    } catch (err: any) {
      setLoadError(err.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, []);

  // auto-load status + page on mount
  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // search resets to page 1 (debounced); page changes reload current query
  useEffect(() => {
    const t = setTimeout(() => loadAvailable(search.trim() || undefined, page), 300);
    return () => clearTimeout(t);
  }, [search, page, loadAvailable]);

  // search change ⇒ drop back to first page
  useEffect(() => {
    setPage(1);
  }, [search]);

  const connect = async (projectUrl: string, role?: Role) => {
    setConnecting(projectUrl);
    try {
      const r = await fetch(`${API}/api/gitlab/connect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectUrl, role }),
      });
      if (r.ok) {
        await loadStatus();
        await loadAvailable(search.trim() || undefined, page);
      }
    } catch (err) {
      console.error('Connect failed:', err);
    } finally {
      setConnecting(null);
    }
  };

  const addManual = async () => {
    if (!manualUrl.trim()) return;
    setAdding(true);
    await connect(manualUrl.trim());
    setManualUrl('');
    setAdding(false);
  };

  const isTokenMissing = loadError?.toLowerCase().includes('token');

  return (
    <div className="gitlab-panel">
      {/* ─── Connection status ───────────────────────────── */}
      {/*
         Three flavours depending on what the backend returned:
         1. New backend with full diagnostics: envPresent / tokenValid — use them
         2. Old backend: only `connected` field — trust it
         3. Nothing reached us (CORS / network) — show offline
      */}
      <div className="gitlab-status-bar">
        <div className="gitlab-status-indicator">
          <span className={`status-dot ${(status.tokenValid || (status.tokenValid === undefined && status.connected)) ? 'status-online' : 'status-offline'}`} />
          <span>
            {status.tokenValid
              ? `GitLab Connected · @${status.tokenUser}`
              : status.tokenValid === undefined && status.connected
              ? 'GitLab Connected (legacy diag)'
              : status.envPresent
              ? 'GitLab Token Invalid'
              : status.envPresent === false
              ? 'GitLab Not Configured'
              : 'GitLab Status Unknown'}
          </span>
        </div>
        {status.url && <span className="gitlab-url">{status.url}</span>}
      </div>

      {/* Diagnostic panel — only show when we have the new fields. */}
      {status.envPresent !== undefined && (
        <div className="gitlab-diag">
          <div className="gitlab-diag-row">
            <span className="gitlab-diag-key">ENV GITLAB_TOKEN</span>
            <span className={`gitlab-diag-val ${status.envPresent ? 'diag-ok' : 'diag-bad'}`}>
              {status.envPresent ? `set (${status.tokenPrefix})` : 'missing'}
            </span>
          </div>
          <div className="gitlab-diag-row">
            <span className="gitlab-diag-key">Token valid (API check)</span>
            <span className={`gitlab-diag-val ${status.tokenValid ? 'diag-ok' : status.envPresent ? 'diag-bad' : 'diag-muted'}`}>
              {status.tokenValid ? `yes · @${status.tokenUser}` : status.envPresent ? `no — ${status.tokenError}` : '—'}
            </span>
          </div>
          <div className="gitlab-diag-row">
            <span className="gitlab-diag-key">GitLab URL</span>
            <span className="gitlab-diag-val">{status.url || '—'}</span>
          </div>
        </div>
      )}

      {/* Hint shown ONLY when backend explicitly confirmed env is missing */}
      {status.envPresent === false && (
        <div className="gitlab-info-box">
          <p>
            <b>GITLAB_TOKEN отсутствует в env пода.</b> Убедись что переменная
            попала в Vault по пути <code>claud1024114/agi-system</code>, и сделай
            <code>kubectl rollout restart deployment/claud1024114-agi-system</code>
            чтобы контейнер подхватил свежие секреты (env читается один раз при
            старте контейнера).
          </p>
        </div>
      )}

      {status.envPresent && status.tokenValid === false && (
        <div className="gitlab-info-box" style={{ borderLeftColor: 'var(--terracotta)', color: 'var(--terracotta)' }}>
          <p>
            <b>Токен установлен, но GitLab его отверг.</b> Перевыпусти PAT на
            https://gitlab.com/-/user_settings/personal_access_tokens со scope
            <code>api</code> + <code>read_repository</code> + <code>write_repository</code>.
          </p>
        </div>
      )}

      {/* ─── Available projects (auto-loaded) ────────────── */}
      <div className="gitlab-available">
        <div className="gitlab-available-head">
          <h3>Репозитории из GitLab</h3>
          <input
            type="search"
            className="gitlab-search"
            placeholder="Поиск по имени…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {loading && <div className="empty-state">Загружаю список…</div>}

        {!loading && loadError && !isTokenMissing && (
          <div className="empty-state">Ошибка: {loadError}</div>
        )}

        {!loading && !loadError && available.length === 0 && (
          <div className="empty-state">
            {search ? 'Ничего не найдено — попробуй другой запрос' : 'GitLab не вернул ни одного проекта для этого токена'}
          </div>
        )}

        {!loading && !loadError && available.length > 0 && (pageInfo.total !== null || pageInfo.hasNext || pageInfo.page > 1) && (
          <div className="gitlab-pagination">
            <button
              type="button"
              className="gitlab-page-btn"
              disabled={pageInfo.page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              aria-label="Предыдущая страница"
            >
              ←
            </button>
            <span className="gitlab-page-info">
              Стр. {pageInfo.page}
              {pageInfo.totalPages ? ` из ${pageInfo.totalPages}` : ''}
              {pageInfo.total !== null ? ` · всего ${pageInfo.total}` : ''}
            </span>
            <button
              type="button"
              className="gitlab-page-btn"
              disabled={!pageInfo.hasNext || loading}
              onClick={() => setPage((p) => p + 1)}
              aria-label="Следующая страница"
            >
              →
            </button>
          </div>
        )}

        {!loading && !loadError && available.length > 0 && (
          <div className="gitlab-avail-list">
            {available.map((p, i) => {
              const role = selectedRole[p.url] || 'backend';
              const isConnecting = connecting === p.url;
              return (
                <motion.div
                  key={p.gitlabId}
                  className={`gitlab-avail-row ${p.connected ? 'avail-connected' : ''}`}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.02, duration: 0.25 }}
                >
                  <div className="avail-left">
                    <div className="avail-name">{p.name}</div>
                    <div className="avail-ns">{p.nameWithNs}</div>
                    {p.description && <div className="avail-desc">{p.description}</div>}
                    <div className="avail-meta">
                      <span>↳ {p.defaultBranch}</span>
                      <span>·</span>
                      <span>{relTime(p.lastActivity)}</span>
                      <span>·</span>
                      <span className={`avail-vis avail-vis-${p.visibility}`}>{p.visibility}</span>
                    </div>
                  </div>
                  <div className="avail-right">
                    {p.connected ? (
                      <span className="avail-badge-connected">Подключён</span>
                    ) : (
                      <>
                        <select
                          className="avail-role"
                          value={role}
                          onChange={(e) => setSelectedRole({ ...selectedRole, [p.url]: e.target.value as Role })}
                        >
                          {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                        <button
                          className="btn-primary avail-connect"
                          onClick={() => connect(p.url, role)}
                          disabled={isConnecting}
                        >
                          {isConnecting ? '…' : 'Подключить'}
                        </button>
                      </>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* ─── Manual URL fallback ─────────────────────────── */}
      <details className="gitlab-add-project">
        <summary>Подключить по URL вручную</summary>
        <div className="gitlab-add-form" style={{ marginTop: 14 }}>
          <input
            type="text"
            className="gitlab-input"
            placeholder="https://gitlab.com/org/project"
            value={manualUrl}
            onChange={(e) => setManualUrl(e.target.value)}
          />
          <button className="btn-primary" onClick={addManual} disabled={adding}>
            {adding ? '…' : 'Подключить'}
          </button>
        </div>
      </details>

      {/* ─── Connected projects ──────────────────────────── */}
      <div className="gitlab-projects">
        <h3>Подключённые к AI</h3>
        {status.projects.length === 0 ? (
          <div className="empty-state">Пока ни одного — выбери из списка выше</div>
        ) : (
          status.projects.map((proj: GitLabProject, i: number) => (
            <motion.div
              key={proj.id}
              className="gitlab-project-card"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              <div className="project-info">
                <div className="project-name">{proj.name}</div>
                <div className="project-url">{proj.url}</div>
                <div className="project-meta">
                  <span>↳ {proj.defaultBranch}</span>
                  {proj.role && <span>role: {proj.role}</span>}
                  {proj.lastCommit && <span>last: {proj.lastCommit}</span>}
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
};
